import React from 'react';

import { defineMessages } from 'react-intl';

import Button from '@atlaskit/button/standard-button';
import EmptyState from '@atlaskit/empty-state';

import { useIntl } from 'src/hooks/intl';
import { useParams } from 'src/router/hooks';
import { useWorkspaceNavActionUrls } from 'src/sections/workspace/components/navigation/use-container-navigation-props';

import emptyStateImage from './file-cabinet-image.svg';

export const messages = defineMessages({
  header: {
    id: 'frontbucket.projectRepositories.emptyState.header',
    description: 'Header for the project repositories page empty state',
    defaultMessage: 'Welcome to your new project',
  },
  headerNoCTA: {
    id: 'frontbucket.projectRepositories.emptyState.headerNoCTA',
    description:
      'Header for the project repositories page empty state when user does not have create repo perm',
    defaultMessage: 'No repositories in this project',
  },
  description: {
    id: 'frontbucket.projectRepositories.emptyState.description',
    description: 'Description for the project repositories page empty state',
    defaultMessage:
      "Now, let's create a repository or add some existing ones to get your project fueled up and ready for launch.",
  },
  descriptionNoCTA: {
    id: 'frontbucket.projectRepositories.emptyState.descriptionNoCTA',
    description:
      'Description for the project repositories page empty state when user does not have create repo perms',
    defaultMessage:
      "Things are pretty quiet right now, but when your team's coding, any new repositories will appear here.",
  },
  createRepository: {
    id: 'frontbucket.projectRepositories.emptyState.button.createRepository',
    description:
      'Button label for trigger to `Create repository` in the current project',
    defaultMessage: 'Create repository',
  },
  addRepositories: {
    id: 'frontbucket.projectRepositories.emptyState.button.addRepositories',
    description:
      'Button label for trigger to `Add repositories` to the current project',
    defaultMessage: 'Add repositories',
  },
});

export const ProjectReposEmptyState = ({
  onAddReposClick,
  isWorkspaceAdmin,
}: {
  onAddReposClick: () => void;
  isWorkspaceAdmin: boolean;
}) => {
  const { workspaceSlug, projectKey } = useParams<{
    workspaceSlug: string;
    projectKey: string;
  }>();
  if (!workspaceSlug || !projectKey) {
    throw new Error(
      'This component must be rendered on a route that includes both :workspaceSlug and :projectKey'
    );
  }

  // permissions are validated on the backend and `createRepoUrl` will only be
  // be defined if user has correct create repo perms
  const { createRepoUrl } = useWorkspaceNavActionUrls();
  const { formatMessage } = useIntl();

  return (
    <EmptyState
      header={
        createRepoUrl
          ? formatMessage(messages.header)
          : formatMessage(messages.headerNoCTA)
      }
      description={
        createRepoUrl
          ? formatMessage(messages.description)
          : formatMessage(messages.descriptionNoCTA)
      }
      imageUrl={emptyStateImage}
      primaryAction={
        createRepoUrl && (
          <Button appearance="primary" href={createRepoUrl}>
            {formatMessage(messages.createRepository)}
          </Button>
        )
      }
      secondaryAction={
        createRepoUrl && isWorkspaceAdmin ? (
          <Button onClick={onAddReposClick}>
            {formatMessage(messages.addRepositories)}
          </Button>
        ) : undefined
      }
    />
  );
};
